import React from 'react';
import PropTypes from 'prop-types';

import { Helmet } from 'core/libs/helmet';
import { withRouter } from 'core/libs/router';
import queryString from 'core/libs/query-string';
import { pure, compose } from 'core/libs/recompose';

import modelPropTypes from 'core/utils/prop-types/model';
import themePropTypes from 'core/utils/prop-types/theme';

import withTopic from 'core/hocs/withTopic';
import withBreakpoint from 'core/hocs/withBreakpoint';
import withTheme from 'core/hocs/withTheme';

import { pageLinkConstructor } from 'core/utils/url-helper';
import resolveRelationships from 'core/utils/relationships';

import TopicPages from 'core/components/TopicPages';
import Erid from 'core/components/Erid';
import TableOfContents from './tableOfContents';
import Widget from './Widget';

import { END_OF_TOPIC_CONTENT } from './constants';

/* eslint-disable import/namespace, import/no-namespace */
import * as blocks from './blocks';

import styles from './index.styl';

const relationships = resolveRelationships(['content'], {});

/**
 * Переменная нужна для фолбека на отсутсвтующие ключи при траверсинге
 * по объектам.
 */
const empty = {};

/**
 * Виджеты, которые сами получают данные из апи, не будем рендерить
 * в тестовом окружении
 */
export const widgetsWithDataProviders = {
  readMore: true,
  galleryLink: true,
  smartTeaser: true,
  pageBreak: true,
};


/**
 * Используется для отрисовки блоков статьи полученной из апи.
 */

function TopicContent(props, context) {
  const {
    content,
    location,
    blocks: customBlocks,
    theme: {
      layout,
      controls: {
        shapka,
        topicContent: {
          tableOfContents: tableOfContentsAtoms,
          showErid,
        },
      },
    },
    amp,
    disableMultipage,
    disableDefaultNavigation,
    topic,
    ctx: siteCtx,
    tableOfContentsTitle,
  } = props;

  const {
    content: topicContent,
    infinityIndex,
  } = topic || empty;

  const widgets = content || relationships(topicContent).content.widgets;

  if (!widgets) return <div className={END_OF_TOPIC_CONTENT} />;

  const {
    host,
  } = context;

  const {
    search,
    pathname,
  } = location;

  const {
    headline,
    announce,
    link,
    is_has_contents: isHasContents,
    is_premium: isPremium,
    erid,
  } = topicContent?.attributes || empty;

  const {
    page: pageNum,
    full,
  } = queryString.parse(search);

  const isFullpage = !!full || amp;
  const isMultipage = widgets.some(widget => widget.type === 'pageBreak') && !disableMultipage;

  // Удаляем разрывы, если нужна полная версия многостраничной статьи
  const fullContent = isFullpage && isMultipage
    ? widgets.filter(widget => widget.type !== 'pageBreak')
    : widgets;

  const pages = fullContent.reduce((result, widget) => {
    const page = result[result.length - 1];

    if (widget.type === 'pageBreak' && !disableMultipage) {
      result.push({ ...widget.attributes, widgets: [] });
    } else {
      const prev = page.widgets[page.widgets.length - 1];
      const prevIsMarkdown = prev?.type === 'markdown';

      // Мерджим виджеты маркдауна которые идут друг за другом.
      if (widget.type === 'markdown' && prevIsMarkdown) {
        page.widgets.pop();
        page.widgets.push({
          ...prev,
          attributes: {
            ...prev.attributes,
            body: prev.attributes.body + '\n\n' + widget.attributes.body,
          },
        });
      } else {
        page.widgets.push(widget);
      }
    }
    return result;
  }, [{ widgets: [], title: 'Начало статьи' }]);

  const activePageIndex = infinityIndex ? 0 : (pageNum || 1) - 1;

  const page = pages[activePageIndex];
  const prevPage = pages[activePageIndex - 1] || empty;
  const nextPage = pages[activePageIndex + 1] || empty;

  const ctx = {
    ...siteCtx,
    chars: (headline || '').length + (announce || '').length,
  };

  const pageWidgets = page.widgets.map(widget => {
    if (process.env.NODE_ENV === 'test' && widgetsWithDataProviders[widget?.type]) return null;
    return widget;
  });

  const widgetContent = pageWidgets.map((block, i) => {
    if (!block) return null;

    const { type } = block;

    const renderer = (customBlocks || empty)[type] || blocks[type];

    if (!renderer) {
      console.warn(`Unsupported content block type '${type}'`);
      return null;
    }

    return (
      <Widget
        key={block.id}
        renderer={renderer}
        parentProps={props}
        block={block}
        ctx={ctx}
        position={i}
      />
    );
  });

  const notFirstPage = page.title && !!activePageIndex;
  const fitScrollToHeadings = shapka.sticky && !!shapka.height;

  return (
    <div className={isPremium ? styles.premium : undefined}>
      <style jsx>{`
        .${styles.premium}
          max-width ${layout.contentColumnMaxWidth}
      `}</style>
      {page && (
        <>
          {isHasContents && !disableDefaultNavigation && (
            <TableOfContents
              widgets={pageWidgets}
              tableOfContentsAtoms={tableOfContentsAtoms}
              tableOfContentsTitle={tableOfContentsTitle}
              {...fitScrollToHeadings && {
                shapkaHeight: shapka.height,
              }}
            />
          )}
          {widgetContent}
          {(showErid && erid) && <Erid erid={erid} />}
          {isMultipage && (
            <Helmet>
              <link
                rel='canonical'
                href={`https://${host}${link || pathname}?full`}
              />
            </Helmet>
          )}
          {pages.length > 1 && (
            <div>
              <Helmet>
                {notFirstPage && (
                  <title>{page.title}</title>
                )}
                {notFirstPage && (
                  <meta
                    property='og:title'
                    content={page.title}
                  />
                )}
                {page.caption && (
                  <meta
                    name='description'
                    content={page.caption}
                  />
                )}
                {page.caption && (
                  <meta
                    property='og:description'
                    content={page.caption}
                  />
                )}
                {!!activePageIndex && (
                  <link
                    rel='prev'
                    href={pageLinkConstructor(activePageIndex, location)}
                  />
                )}
                {activePageIndex < pages.length - 1 && (
                  <link
                    rel='next'
                    href={pageLinkConstructor(activePageIndex + 2, location)}
                  />
                )}
              </Helmet>
              <div style={{ clear: 'both' }}>
                <TopicPages
                  link={link}
                  active={activePageIndex + 1}
                  total={pages.length}
                  prevTitle={prevPage.title}
                  nextTitle={nextPage.title}
                  infinity={infinityIndex > 0}
                />
              </div>
            </div>
          )}
        </>
      )}
      <div className={END_OF_TOPIC_CONTENT} />
    </div>
  );
}

const adsPropTypes = PropTypes.arrayOf(PropTypes.shape({
  /**
   * Необходимое кол-во символов перед рекламным местом.
   * Реальное кол-во символов может отличаться в большую сторону, так
   * как рекламное место располагается строго между параграфами.
   * По умолчанию: `200`.
   *
   * Аналог параметра `offset` из модуля `rds-content-banners`.
   */
  before: PropTypes.number,

  /**
   * Необходимое кол-во символов, которое должно оставаться после рекламного
   * места до конца виджета.
   * По умолчанию: `0`.
   *
   * Аналог параметра `haveToBeAtLeast` из модуля `rds-content-banners`.
   */
  after: PropTypes.number,

  /**
   * Отображать рекламное место лишь один раз за статью.
   * По умолчанию: `false`.
   *
   * Параметр противоположный `looped` из модуля `rds-content-banners`.
   */
  showOnlyOnce: PropTypes.bool,

  /**
   * Признак того, что контент должен обтекать рекламный блок.
   * Влияет на расчет места расположения рекламного места.
   * По умолчанию: `false`.
   */
  float: PropTypes.bool,

  /**
   * Компонент отвечающий за отрисовку рекламного места.
   *
   * В качестве пропсов будет передан следующий контракт:
   * {
   *   index: Number, // Индекс рекламного места в теле топика
   * }
   */
  renderer: PropTypes.func.isRequired,
}));

TopicContent.contextTypes = {
  host: PropTypes.string,
};

TopicContent.propTypes = {
  /**
   * Этот пропс используется для того, чтобы отключить многостраничность.
   * Все страницы статьи будут выведены на первой странице.
   *
   * TODO: Это временные меры. В дальнейшем нужно сделать поддержку
   * многостраничности на амп.
   */
  amp: PropTypes.bool,

  /**
   * Массив блоков располагающийся в атрибуте `widget` relationship `content`.
   */
  content: PropTypes.arrayOf(modelPropTypes(PropTypes.object)),

  /** @ignore */
  topic: PropTypes.object.isRequired,

  /**
   * Справочник кастомных блоков объявленные пользователями.
   *
   * Имеет больший приоритет чем встроенные блоки.
   */
  blocks: PropTypes.objectOf(PropTypes.func),

  // Список рекламных мест в статье для десктопного лейаута.
  desktopAds: adsPropTypes,

  // Список рекламных мест в статье для мобильного лейаута.
  mobileAds: adsPropTypes,

  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * Параметр запрещает "разрывать" топик на несколько страниц с помощью виджета `pageBreak`.
   * При этом сам виджет сохраняется в дереве рендера.
   */
  disableMultipage: PropTypes.bool,

  disableDefaultNavigation: PropTypes.bool,
  /**
   * Параметр ctx доступен в виджетах на сайтах
   */
  ctx: PropTypes.object,

  /**
   * Текст заголовка оглавления
   */
  tableOfContentsTitle: PropTypes.string,

  theme: themePropTypes(`{
    controls: {
      shapka: {
        sticky,
        height,
      },
      topicContent: {
        tableOfContents: {
          borderBottom,
          padding,
          margin,
          maxWidth,
          listPadding,
          listStyle,
          title: {
            font,
            color,
            display,
          },
          link: {
            color,
            borderBottom,
            hover: {
              borderBottom,
            }
          },
        }
      }
    },
  }`),
};

const TopicContentWithHOCs = compose(
  withTopic,
  withRouter,
  withBreakpoint,
  withTheme,
  pure,
)(TopicContent);
TopicContentWithHOCs.displayName = 'TopicContent';

export { TopicContent as StorybookComponent };
export default TopicContentWithHOCs;
